<template>
    <NuxtLayout name="main" :dark="true" :favorite="true">
        <!-- * 确认窗口 -->
        <confirm-modal ref="confirm" @confirm="methods.submit" :content="$t('AAVCapsidViabilityNotFasta')" />
        <!-- * 线索追踪表单 -->
        <consult-form ref="form" @submit="methods.submit" />
        <!-- * 页面内容 -->
        <div class="container !mt-[120px]" :class="css.container">
            <section class="text-emphas text-center font-bold">
                <h1 class="text-[40px] leading-[72px]">{{ $t("AAVCapsidViability") }}</h1>
                <h2 class="text-[16px] leading-[32px]">{{ $t("AAVCapsidViabilityDescript") }}</h2>
                <div class="flex justify-end gap-x-[8px] text-default font-normal mt-[16px]">
                    <ProjectRoute
                        class="t-delay cursor-pointer underline underline-offset-2 hover:text-main"
                        to="https://doi.org/10.1016/j.omta.2026.201806">
                        {{ $t("Publication") }}
                    </ProjectRoute>
                    <span> | </span>
                    <ProjectRoute class="t-delay cursor-pointer underline underline-offset-2 hover:text-main" :to="{ name: 'AAVViabilityManual' }">
                        {{ $t("Tutorials") }}
                    </ProjectRoute>
                </div>
            </section>

            <section class="mt-[8px]" :class="css.contents">
                <div class="flex-1 pt-[32px] px-[40px] pb-[32px] bg-color7">
                    <h2 class="text-[24px] text-emphas leading-[32px] font-bold">{{ $t("DataInput") }}</h2>
                    <!-- * 模式选择器 -->
                    <div class="mt-[16px]">
                        <div class="mb-[4px]">{{ $t("Sereotype") }}</div>
                        <ui-select v-model="sereotype" width="100%" :candidates="vars.sereotype" />
                    </div>

                    <!-- * 预览内容 -->
                    <div class="mt-[8px]">
                        <div class="mb-[4px]">{{ message }}</div>
                        <img class="w-full object-cover" src="./src/images/view.webp" alt="Pre-View" />
                    </div>

                    <!-- * 输入框 -->
                    <ui-textarea v-model="context" :height="244" :class="css.textarea" style="transform: translateY(-2px)" />
                    <!-- * 交互按钮 -->
                    <div class="flex justify-between items-center h-[40px] mt-[6px]">
                        <div class="flex items-center">
                            <div class="flex items-center t-delay cursor-pointer hover:text-main" @click="methods.demo">
                                <img class="mr-[4px]" src="./src/images/data.webp" alt="Data" />
                                <div class="underline underline-offset-2">{{ $t("DemoData") }}</div>
                            </div>

                            <div class="flex items-center ml-[24px] t-delay cursor-pointer hover:text-main" @click="methods.upload">
                                <img class="mr-[4px]" src="./src/images/upload.webp" alt="Data" />
                                <div class="underline underline-offset-2">{{ $t("UploadAFile") }}</div>
                            </div>
                        </div>

                        <ui-button :class="css.formats" @click="methods.format"> {{ $t("FormatAsFasta") }} </ui-button>
                    </div>

                    <!-- * 分割线 -->
                    <div class="my-[20px] w-full h-[1px] bg-uicolor4"></div>
                    <!-- * 交互按钮 -->
                    <div>
                        <ui-button :class="css_button.cancel" :width="112" @click="methods.reset"> {{ $t("Reset") }} </ui-button>
                        <ui-button class="ml-[16px]" :width="112" @click="methods.validator"> {{ $t("Predict") }} </ui-button>
                    </div>
                </div>

                <div :class="css.next"></div>
                <div class="flex-1 pt-[32px] px-[40px] pb-[32px] bg-color7">
                    <div class="flex items-center">
                        <h2 class="text-[24px] text-emphas leading-[32px] font-bold">{{ $t("PredictionResults") }}</h2>
                        <span class="ml-[8px]">{{ $t("GeneratedByAI") }}</span>
                    </div>

                    <!-- * 结果图表 -->
                    <diagram class="mt-[24px]" :class="css.diagram" :data="data" :option="option">
                        <template #sequence="{ data }">
                            <div class="truncate">{{ data.sequence }}</div>
                        </template>
                        <template #empty>
                            <div class="pt-[80px] pb-[160px] my-[-5px] mx-[-16px] text-center bg-uicolor4/10">
                                <img class="mx-auto" src="./src/images/nots.webp" alt="Not Data" />
                                <div class="mt-[16px]">{{ $t("NoData") }}</div>
                            </div>
                        </template>
                    </diagram>

                    <!-- * 免责声明 -->
                    <div class="flex items-start" :class="css.disclaimer">
                        <img src="./src/images/star.webp" alt="" />
                        <span>{{ $t("AAVCapsidViabilityDisclaimer") }}</span>
                    </div>

                    <ui-button class="mt-[18px]" :width="112" v-show="data.length" @click="methods.download"> {{ $t("DownloadResult") }} </ui-button>
                </div>
            </section>

            <!-- * 底部文献说明 -->
            <section class="mt-[72px] max-lg:mt-[64px]" :class="css.publication">
                <div class="text-emphas text-[16px] font-bold leading-[24px] text-center">{{ $t("AAVCapsidViabilityPublicationTitle") }}</div>
                <div class="mt-[20px]" :class="$i18n.locale != 'en' && 'indent-[2em]'">
                    <i18n-t tag="span" scope="global" keypath="AAVCapsidViabilityPublicationContent">
                        <template #journal
                            ><i>{{ $t("AAVCapsidViabilityPublicationJournal") }}</i></template
                        >
                        <template #emphas1
                            ><b class="text-emphas">{{ $t("AAVCapsidViabilityPublicationEmphas1") }}</b></template
                        >
                        <template #emphas2
                            ><b class="text-emphas">{{ $t("AAVCapsidViabilityPublicationEmphas2") }}</b></template
                        >
                        <template #break><br /></template>
                    </i18n-t>
                    <ProjectRoute :to="$t('AAVCapsidViabilityPublicationUrl')" target="_blank" rel="noopener noreferrer">
                        <u class="text-[#72299DFF] underline-offset-4">{{ $t("AAVCapsidViabilityPublicationLink") }}</u>
                    </ProjectRoute>
                </div>
            </section>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
//* 组件库
import { UiSelect, UiButton, UiTextarea } from "various-ui";
//* 公共组件
import { Diagram } from "@/components/visualizations";
import { ConfirmModal } from "@/components/windows";
//* 私有组件
import { ConsultForm } from "./components";
//* 组件属性
import { useComposable } from "./src/composable";
//* 样式
import css from "./index.module.less";
import css_button from "@/assets/style/src/button.module.less";

//* 组合函数
const { vars, refs, nodes, methods, computeds } = useComposable();
const { message } = computeds;
const { confirm } = nodes;
const { data, option, context, sereotype } = refs;

onMounted(() => methods.init());
onBeforeUnmount(() => {
    vars.observer?.disconnect();
    vars.controller?.abort();
    if (vars.timeout) {
        clearTimeout(vars.timeout);
    }
});
</script>
